import type {Wallet} from '@stripe-internal/wallet-connect-wrapper';
import type {SupportedCurrency, Screen} from 'src/v2/state/types';
import {getWalletNetworkSupport} from 'src/v2/components/wallet/getWalletNetworkSupport';
import {logger} from 'src/utils/logger';

type Params = {
  activeWallet: Wallet | null;
  supportedCurrencies: SupportedCurrency[];
  connectedWallets: Wallet[];
};

type Result =
  | {
      redirect: Screen;
    }
  | {
      activeWallet: Wallet;
    };

/**
 * Validates wallet state, returning either a screen to redirect to or the validated wallet.
 * Returns {redirect: Screen} if validation fails, or {activeWallet: Wallet} if validation passes.
 */
export const validateWalletForCheckout = ({
  activeWallet,
  supportedCurrencies,
  connectedWallets,
}: Params): Result => {
  // Check if we have an active wallet first
  if (!activeWallet) {
    logger.log('crypto_payin_session.error', {
      error_code: 'missing_active_wallet_for_checkout',
    });
    return {
      redirect: connectedWallets.length > 1 ? 'WALLET_SWITCH' : 'WALLET_SELECT',
    };
  }

  // Check network support for the active wallet
  const networkSupport = getWalletNetworkSupport(
    activeWallet,
    supportedCurrencies,
  );

  if (networkSupport === 'not_supported') {
    // Network is unsupported -> WALLET_SWITCH
    return {redirect: 'WALLET_SWITCH'};
  }

  if (networkSupport === 'network_type_supported') {
    // Network partially supported -> NETWORK_SWITCH
    return {redirect: 'NETWORK_SWITCH'};
  }

  return {activeWallet};
};
